import { NODE_TYPES } from 'modules/workflows/constants';

import { compact } from 'lodash';
import { addEdge, addNode, getEdges, getNodes, openNodeRequest } from 'modules/workflows/ducks';
import { Edge, Node } from 'modules/workflows/types';
import { useDispatch, useSelector } from 'react-redux';

/*
Hey! You can automate journey creation programmatically using this API.

It's the exact same thing as using drag-and-drop because both use the same redux actions.

All functions reside on window

With this, you can:
- add a node to a graph
- open the node editor by title
- connect two nodes by their title
- clone anyone's journey node skeleton (no data in the actual nodes, just the nodes themselves)
- paste the journey node skeleton into the console and fully re-create that journey (minus node data!!)
- automate building a large journey by adding a ton of nodes and using getNextAvailableOutputConnector for edge connection
- create and share journeys!

There are some quirks, however. In the future, we could mitigate them by using node ID's instead:
- if node titles aren't unique, you'll always select the first node with the duplicate name
- on cloning journey skeleton, I add an index number to the title to ensure uniqueness.
--- this is required for properly connecting the edges
- node ID's are a pain to work with, I only use node titles to identify nodes
--- yeah, you need unique node titles for everything.
--- Feel free to change it in the future, but it's messy. For now, just have unique titles! Use a loop index or something

*/

declare global {
  interface Window {
    addNodeToGraph: (title: string, nodeType: string, x?: number, y?: number) => void;
    nodeTypes: Record<string, string>;
    getNodesByTitle: (title: string) => Node[] | undefined;
    connectNodesByTitle: (title1: string, title2: string, sourceAnchor: number) => void;
    getOutputConnectionsCount: (node: Node) => void;
    openNodeEditor: (node: Node) => void;
    openNodeEditorByTitle: (title: string) => void;
    getNextAvailableOutputConnector: (title: string) => string | undefined;
    getAllAvailableOutputConnectors: (node: Node) => string[] | undefined;
    cloneJourneySkeleton: () => string;
    getNodesByType: (type: string) => Node[];
  }
}

// standalone hook for easy drop-in.
export const useJourneyPowerTools = () => {
  const nodes = useSelector(getNodes);
  const edges = useSelector(getEdges);

  const dispatch = useDispatch();

  if (process.env.NODE_ENV === 'production') {
    return {};
  }

  function addNodeToGraph(title: string, nodeType: string, x?: number, y?: number) {
    const nodeCount = nodes?.length || 0;
    const MAX = 4;
    // if user doesn't provide X, lets just throw it up there somewhere decent
    const randomX = (nodeCount % MAX) * 300 + 100;
    const random1to100 = Math.floor(Math.random() * 100) + 1;
    // why add random1to100? bc otherwise the UI gets weird with edge display
    const randomY = Math.floor(nodeCount / MAX) * 350 + random1to100;
    // I'm really not sure this ID is even necessary, but typescript complains without it
    dispatch(
      addNode({
        type: nodeType,
        x: x || randomX,
        y: y || randomY,
        id: nodes.length.toString(),
        title,
      }),
    );
  }
  function getNodesByType(type: string) {
    return nodes.filter((node) => node.type === type);
  }
  function getNodesByTitle(title: string) {
    const nodesByTitle = nodes.filter((node) => node.title === title);
    if (nodesByTitle.length === 0) {
      /* eslint-disable no-console */
      console.error(`Node doesnt exist for title ${title}`);
    } else {
      return nodesByTitle as Node[];
    }
  }

  function getOutputConnectionsCount(node: Node) {
    return node?.outputConnectors?.length || 0;
  }

  function openNodeEditor(node: Node) {
    dispatch(openNodeRequest(node.id, {}));
  }

  function openNodeEditorByTitle(title: string) {
    const node = getNodesByTitle(title)?.[0];
    if (node) {
      openNodeEditor(node);
    }
  }

  function getAllAvailableOutputConnectors(node: Node) {
    const nodeId = node.id;
    const nodeEdges = edges.filter((edge: Edge) => edge.source === nodeId);
    const takenSourceAnchors = new Set(nodeEdges.map((edge: Edge) => `${edge.sourceAnchor}`));
    const nodeOutputConnectors = (node?.outputConnectors || [])?.map(
      (_: any, i: number) => `${i + 1}`,
    );
    const remainingNodeOutputConnectors = nodeOutputConnectors?.filter(
      (con: string) => !takenSourceAnchors.has(con),
    );

    return remainingNodeOutputConnectors;
  }

  function getNextAvailableOutputConnector(title: string) {
    const node = getNodesByTitle(title)?.[0];
    if (!node) return undefined;
    const remaining = getAllAvailableOutputConnectors(node);
    if (remaining?.length === 0) {
      // return false so the coder can do something else if the node doesnt have remaining connectors
      // getNextAvailableOutputConnector("title") ? doConnect() : onToNextNode()
      return undefined;
    }
    return remaining?.[0];
  }
  function cloneJourneySkeleton() {
    // get all nodes, get all edges
    // modify title so we can ensure uniqueness for adding edges
    // for each node, return "addNodeToGraph(`${node.title} ${idx}`,node.type)"

    const nodeTitleMap = {} as Record<string, string>;
    const addNodeCommands = nodes.map((node, i) => {
      // special exception for start node, which we don't re-create and so is named "Start"
      const newNodeTitle =
        node.type === NODE_TYPES.START ? 'Start' : (`${node.title} ${i}` as string);
      nodeTitleMap[node.id] = newNodeTitle;
      // skip the start node!
      return node.type === NODE_TYPES.START
        ? ''
        : `addNodeToGraph("${newNodeTitle}", "${node.type}", ${node.x}, ${node.y})`;
    });

    // cant count on just grabbing node id after addNodeToGraph.
    // instead, modify title
    // for each edge, return "connectNodesByTitle(node1.title, node2.title, edge.sourceAnchor)"
    const addEdgeCommands = edges.map((edge: Edge) => {
      const { sourceAnchor, source, target } = edge;
      // get the title of the source and target node by using old nodes data
      const node1 = nodes.find((node) => node.id === source);
      const node2 = nodes.find((node) => node.id === target);
      return `connectNodesByTitle("${nodeTitleMap[node1?.id as string]}", "${
        nodeTitleMap[node2?.id as string]
      }", ${sourceAnchor})`;
    });
    return compact([...addNodeCommands, ...addEdgeCommands]).join('; ');
  }
  function connectNodesByTitle(title1: string, title2: string, sourceAnchor: number) {
    const node1 = getNodesByTitle(title1)?.[0];
    const node2 = getNodesByTitle(title2)?.[0];
    if (!node1 || !node2) {
      /* eslint-disable no-console */
      console.error('Node not found by title, double check them');
    } else {
      const actualSourceAnchor = sourceAnchor || getNextAvailableOutputConnector(title1);

      // note: sourceAnchor is an index 0...n of each output connector.
      dispatch(
        addEdge({
          source: node1.id,
          target: node2.id,
          sourceAnchor: actualSourceAnchor,
          targetAnchor: 0,
        } as Edge),
      );
    }
  }
  window.addNodeToGraph = addNodeToGraph;
  window.nodeTypes = NODE_TYPES;
  window.getNodesByTitle = getNodesByTitle;
  window.connectNodesByTitle = connectNodesByTitle;
  window.getOutputConnectionsCount = getOutputConnectionsCount;
  window.openNodeEditor = openNodeEditor;
  window.openNodeEditorByTitle = openNodeEditorByTitle;
  window.getNextAvailableOutputConnector = getNextAvailableOutputConnector;
  window.getAllAvailableOutputConnectors = getAllAvailableOutputConnectors;
  window.cloneJourneySkeleton = cloneJourneySkeleton;
  window.getNodesByType = getNodesByType;
};
